import { Controller } from "@hotwired/stimulus";
import {
  applyPollingResponse,
  beginPollingRequest,
  createPollingState,
  finishPollingRequest,
  invalidatePollingRequests,
  pollingAllowed,
  retryDelayMilliseconds
} from "../components/exam-waiting-room-polling";

export default class extends Controller {
  static targets = [
    "waitingRoom", "runs", "activity", "status", "announcement", "windowStatus",
    "reentrySection", "reentryPanels", "reentryPanelTemplate"
  ];
  static values = {
    url: String,
    waitingRoomRevision: String,
    runsRevision: String,
    pendingCount: Number
  };

  connect() {
    this.pollingState = createPollingState();
    this.timer = null;
    this.abortController = null;
    this.actionInFlight = false;
    this.reentryCapabilities = new Map();
    this.reentryCountdownTimer = null;
    this.visibilityHandler = () => this.visibilityChanged();
    document.addEventListener("visibilitychange", this.visibilityHandler);
    this.refresh();
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    this.stopTimer();
    this.stopReentryCountdown();
    this.clearReentryCapabilities();
    this.abortCurrentRequest();
  }

  visibilityChanged() {
    if (document.hidden) {
      this.stopTimer();
      return;
    }

    this.renderReentryCapabilities();
    this.refresh({ replaceInFlight: true });
  }

  async refresh({ replaceInFlight = false, preserveStatus = false } = {}) {
    if (this.actionInFlight) return false;
    if (!this.hasUrlValue || !pollingAllowed({ hidden: document.hidden, inFlight: this.pollingState.inFlight })) {
      if (!(replaceInFlight && this.pollingState.inFlight)) return false;
    }
    if (replaceInFlight) this.abortCurrentRequest();

    this.stopTimer();
    const sequence = beginPollingRequest(this.pollingState);
    if (!sequence) return false;
    const controller = new AbortController();
    this.abortController = controller;
    this.activityTarget.textContent = "Aktualizowanie…";

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
        signal: controller.signal
      });
      if (response.redirected || response.status === 401) {
        this.stop("Sesja wygasła. Zaloguj się ponownie.");
        if (response.redirected) window.location.assign(response.url);
        return false;
      }
      if (response.status === 404) {
        this.stop("Poczekalnia nie jest już dostępna.");
        return false;
      }
      if (response.status === 429) {
        this.showTransientError("Odświeżanie zostało chwilowo ograniczone.");
        this.schedule(retryDelayMilliseconds(response.headers.get("Retry-After")));
        return false;
      }
      if (response.status === 503) {
        this.showTransientError("Poczekalnia jest chwilowo niedostępna. Spróbujemy ponownie.");
        this.schedule(5000);
        return false;
      }
      if (!response.ok) throw new Error("waiting_room_request_failed");

      const payload = await response.json();
      return applyPollingResponse(
        this.pollingState,
        sequence,
        () => this.applyPayload(payload, { preserveStatus })
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        this.showTransientError("Nie udało się odświeżyć poczekalni. Spróbujemy ponownie.");
        this.schedule(5000);
      }
      return false;
    } finally {
      if (this.abortController === controller) {
        this.abortController = null;
        finishPollingRequest(this.pollingState, sequence);
        if (!this.timer && !document.hidden) this.schedule();
      }
    }
  }

  async submitAction(event) {
    event.preventDefault();
    if (this.actionInFlight) return;
    const form = event.currentTarget;
    const confirmation = form.dataset.confirmation;
    if (confirmation && !window.confirm(confirmation)) return;

    const button = event.submitter || form.querySelector("button[type='submit']");
    if (button?.disabled) return;
    if (button) {
      button.disabled = true;
      button.classList.add("cursor-wait", "opacity-60");
    }
    this.actionInFlight = true;

    this.abortCurrentRequest();
    this.stopTimer();
    this.activityTarget.textContent = "Aktualizowanie…";

    try {
      const response = await fetch(form.action, {
        method: form.method || "post",
        body: new FormData(form),
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      });
      if (response.redirected) {
        this.stop("Sesja wygasła. Zaloguj się ponownie.");
        window.location.assign(response.url);
        return;
      }
      if (response.status === 401) return this.stop("Sesja wygasła. Zaloguj się ponownie.");
      if (response.status === 404) return this.stop("Poczekalnia nie jest już dostępna.");
      if (response.status === 429) {
        this.showTransientError("Operacja została chwilowo ograniczona.");
        this.schedule(retryDelayMilliseconds(response.headers.get("Retry-After")));
        return;
      }
      if (response.status === 503) {
        this.showTransientError("Operacja jest chwilowo niedostępna. Spróbuj ponownie.");
        this.schedule(5000);
        return;
      }

      let payload;
      try {
        payload = await response.json();
      } catch (_error) {
        this.showTransientError("Serwer zwrócił nieprawidłową odpowiedź. Odświeżymy aktualny stan.");
        this.announcementTarget.textContent = "Operacja nie została potwierdzona.";
        this.actionInFlight = false;
        await this.refresh({ replaceInFlight: true, preserveStatus: true });
        return;
      }

      if (response.status === 422) {
        const message = payload.message || "Nie można wykonać tej operacji dla aktualnego stanu zgłoszenia.";
        this.actionInFlight = false;
        await this.refresh({ replaceInFlight: true, preserveStatus: true });
        this.statusTarget.textContent = message;
        this.announcementTarget.textContent = "Operacja nie została wykonana.";
        return;
      }
      if (!response.ok) {
        this.showTransientError("Nie udało się wykonać operacji. Odśwież stronę i spróbuj ponownie.");
        this.announcementTarget.textContent = "Operacja nie została wykonana.";
        return;
      }
      if (payload.ok === false) {
        this.actionInFlight = false;
        await this.refresh({ replaceInFlight: true, preserveStatus: true });
        this.statusTarget.textContent = payload.message || "Operacja nie została wykonana.";
        this.announcementTarget.textContent = "Operacja nie została wykonana.";
        return;
      }

      this.announcementTarget.textContent = payload.message || "Poczekalnia została zaktualizowana.";
      if (payload.reentry_capability) {
        this.storeReentryCapability(payload);
      }
      this.actionInFlight = false;
      await this.refresh({ replaceInFlight: true });
      this.renderReentryCapabilities();
    } catch (_error) {
      this.showTransientError("Nie udało się wykonać operacji. Spróbuj ponownie.");
      this.schedule(5000);
    } finally {
      this.actionInFlight = false;
      if (button?.isConnected) {
        button.disabled = false;
        button.classList.remove("cursor-wait", "opacity-60");
      }
    }
  }

  applyPayload(payload, { preserveStatus = false } = {}) {
    const previousPendingCount = this.pendingCountValue;
    if (payload.waiting_room_revision !== this.waitingRoomRevisionValue) {
      this.replaceTarget(this.waitingRoomTarget, payload.waiting_room_html);
      this.waitingRoomRevisionValue = payload.waiting_room_revision || "";
    }
    if (payload.runs_revision !== this.runsRevisionValue) {
      this.replaceTarget(this.runsTarget, payload.runs_html);
      this.runsRevisionValue = payload.runs_revision || "";
    }

    this.pendingCountValue = Number(payload.pending_count) || 0;
    if (this.pendingCountValue > previousPendingCount) {
      this.announcementTarget.textContent = "Nowe zgłoszenie oczekuje.";
    }
    this.updateWindowStatus(payload);
    this.reconcileActiveReentryCapabilities(payload.active_reentry_exam_quiz_ids);
    if (!preserveStatus) this.statusTarget.textContent = "";
    this.renderReentryCapabilities();
    this.activityTarget.textContent = `Ostatnia aktualizacja: ${this.formattedTime(payload.server_time)}`;
  }

  storeReentryCapability(payload) {
    const runId = String(payload.reentry_exam_quiz_id || "");
    const capability = payload.reentry_capability_display || payload.reentry_capability;
    const expiresAt = Date.parse(payload.reentry_expires_at);
    const serverTime = Date.parse(payload.server_time);
    const observedNow = this.now();
    if (!runId || !capability || !Number.isFinite(expiresAt)) return;

    const authoritativeNow = Number.isFinite(serverTime) ? Math.max(observedNow, serverTime) : observedNow;
    const deadline = observedNow + Math.max(0, expiresAt - authoritativeNow);
    const existingPanel = this.reentryPanel(runId);
    if (existingPanel) existingPanel.querySelector('[data-reentry-role="copy-feedback"]').textContent = "";
    this.reentryCapabilities.set(runId, {
      runId,
      capability,
      participant: this.reentryParticipantLabel(payload.reentry_participant || {}),
      deadline
    });
  }

  renderReentryCapabilities() {
    const now = this.now();
    for (const [runId, entry] of this.reentryCapabilities) {
      if (entry.deadline <= now) this.removeReentryCapability(runId);
    }

    for (const entry of this.reentryCapabilities.values()) {
      const panel = this.reentryPanel(entry.runId);
      const currentPanel = panel || this.appendReentryPanel(entry.runId);
      currentPanel.querySelector('[data-reentry-role="participant"]').textContent = entry.participant;
      currentPanel.querySelector('[data-reentry-role="code"]').textContent = entry.capability;
      currentPanel.querySelector("button").dataset.reentryRunId = entry.runId;
      currentPanel.querySelector('[data-reentry-role="countdown"]').textContent =
        this.reentryCountdownLabel(entry.deadline - now);
      if (!panel) currentPanel.querySelector('[data-reentry-role="copy-feedback"]').textContent = "";
    }

    this.reentrySectionTarget.hidden = this.reentryCapabilities.size === 0;
    if (this.reentryCapabilities.size > 0) this.startReentryCountdown();
    else this.stopReentryCountdown();
  }

  async copyReentryCapability(event) {
    const runId = event.currentTarget.dataset.reentryRunId;
    const entry = this.reentryCapabilities.get(runId);
    const panel = this.reentryPanel(runId);
    if (!entry || !panel) return;

    const feedback = panel.querySelector('[data-reentry-role="copy-feedback"]');

    try {
      await navigator.clipboard.writeText(entry.capability);
      feedback.textContent = "Skopiowano";
    } catch (_error) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(panel.querySelector('[data-reentry-role="code"]'));
      selection.removeAllRanges();
      selection.addRange(range);
      feedback.textContent = "Kod został zaznaczony. Skopiuj go ręcznie.";
    }
  }

  reconcileActiveReentryCapabilities(activeRunIds) {
    if (!Array.isArray(activeRunIds)) return;

    const active = new Set(activeRunIds.map(String));
    for (const runId of this.reentryCapabilities.keys()) {
      if (!active.has(runId)) this.removeReentryCapability(runId);
    }
  }

  reentryParticipantLabel(participant) {
    const name = [participant.first_name, participant.last_name].map((value) => String(value || "").trim())
      .filter(Boolean).join(" ");
    const identifier = String(participant.student_identifier || "").trim();
    if (name && identifier) return `${name} · nr ${identifier}`;
    if (name) return name;
    if (identifier) return `nr ${identifier}`;
    return "Uczeń (brak danych)";
  }

  reentryCountdownLabel(milliseconds) {
    const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  }

  reentryPanel(runId) {
    return Array.from(this.reentryPanelsTarget.children)
      .find((panel) => panel.dataset.reentryRunId === String(runId));
  }

  appendReentryPanel(runId) {
    const panel = this.reentryPanelTemplateTarget.content.firstElementChild.cloneNode(true);
    panel.dataset.reentryRunId = String(runId);
    this.reentryPanelsTarget.appendChild(panel);
    return panel;
  }

  removeReentryCapability(runId) {
    const entry = this.reentryCapabilities.get(String(runId));
    if (entry) entry.capability = null;
    this.reentryCapabilities.delete(String(runId));
    const panel = this.reentryPanel(runId);
    if (panel) {
      panel.querySelector('[data-reentry-role="code"]').textContent = "";
      panel.remove();
    }
  }

  clearReentryCapabilities() {
    for (const runId of Array.from(this.reentryCapabilities.keys())) this.removeReentryCapability(runId);
    if (this.hasReentrySectionTarget) this.reentrySectionTarget.hidden = true;
  }

  startReentryCountdown() {
    if (this.reentryCountdownTimer) return;
    this.reentryCountdownTimer = window.setInterval(() => this.renderReentryCapabilities(), 1000);
  }

  stopReentryCountdown() {
    if (!this.reentryCountdownTimer) return;
    window.clearInterval(this.reentryCountdownTimer);
    this.reentryCountdownTimer = null;
  }

  now() {
    return Date.now();
  }

  replaceTarget(target, html) {
    const focusKey = document.activeElement?.dataset?.focusKey;
    target.innerHTML = html || "";
    if (focusKey) target.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`)?.focus();
  }

  updateWindowStatus(payload) {
    if (!payload.window_open) {
      this.windowStatusTarget.textContent = "Dołączanie zakończone.";
      return;
    }

    const closesAt = new Date(payload.window_closes_at);
    const serverTime = new Date(payload.server_time);
    const remainingMinutes = Math.max(1, Math.ceil((closesAt - serverTime) / 60_000));
    this.windowStatusTarget.textContent =
      `Dołączanie otwarte do ${this.formattedTime(payload.window_closes_at, false)}. Pozostało około ${remainingMinutes} min.`;
  }

  formattedTime(value, seconds = true) {
    const options = { hour: "2-digit", minute: "2-digit" };
    if (seconds) options.second = "2-digit";
    return new Intl.DateTimeFormat("pl-PL", options).format(new Date(value));
  }

  showTransientError(message) {
    this.statusTarget.textContent = message;
    this.activityTarget.textContent = "Ostatnia poprawna lista pozostaje widoczna.";
  }

  stop(message) {
    this.stopTimer();
    this.abortCurrentRequest();
    this.statusTarget.textContent = message;
  }

  schedule(delay = 2500) {
    if (this.timer || document.hidden) return;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.refresh();
    }, delay);
  }

  stopTimer() {
    if (!this.timer) return;
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  abortCurrentRequest() {
    this.abortController?.abort();
    this.abortController = null;
    invalidatePollingRequests(this.pollingState);
  }
}
